import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import arrowIcon from '@/assets/icons/arrowIconWhite.svg';
import copyIcon from '@/assets/icons/copyIcon.svg';
import replyAllIcon from '@/assets/icons/replyAllIcon.svg';
import replyIcon from '@/assets/icons/replyIcon.svg';
import { Button } from '@/components/ui/button';
import {
  displayReplyAllWithContent,
  displayReplyWithContent,
  isInReadMode,
  isReadingDraft,
  openOrUpdateComposeWindow,
} from '@/config/office/office';
import type { EmailDraft } from '@/utils/emailContentDisplayUtils';
import { useCopyToClipboard } from '@/utils/hooks/useCopyToClipboard';
import { useTypewriter } from '@/utils/hooks/useTypewriter';

interface EmailDraftCardProps {
  draft: EmailDraft;
  isGenerating?: boolean;
}

const addBlueColorToBody = (body: string): string => {
  const responseRegex = /^(\s*|<[^>]+>\s*)(Response \[?([A-Z]{1,4})]?:)(.*)$/;
  return body
    .split('\n')
    .map((line) => {
      const match = responseRegex.exec(line);
      if (match) {
        return `${match[1]}<span style="color: var(--color-link, #145fc4)">${match[2]}${match[4]}</span>`;
      }
      return line;
    })
    .join('\n');
};

const getUserInitials = (): string => {
  if (typeof Office !== 'undefined' && Office.context?.mailbox?.userProfile) {
    const displayName = Office.context.mailbox?.userProfile?.displayName || '';
    return displayName
      .split(' ')
      .filter((n) => n.length > 0)
      .map((n) => n?.[0]?.toUpperCase())
      .join('');
  }
  return '';
};

const formatBodyWithColoredResponses = (body: string, userInitials: string) => {
  const lines = body.split('\n');
  return lines.map((line, index) => {
    const responseRegex = /^(\s*|<[^>]+>\s*)(Response \[?([A-Z]{1,4})\]?:)(.*)$/;
    const responseMatch = responseRegex.exec(line);
    if (responseMatch) {
      const prefix = responseMatch[1] ?? '';
      const responseLabel = responseMatch[2] ?? '';
      const initials = responseMatch[3] ?? '';
      const responseText = responseMatch[4] ?? '';
      return (
        <div className="ml-2" key={index}>
          <span dangerouslySetInnerHTML={{ __html: prefix }} />
          <span
            className={initials.toUpperCase() === userInitials.toUpperCase() ? 'text-link' : ''}
            dangerouslySetInnerHTML={{ __html: responseLabel + responseText }}
          />
        </div>
      );
    }
    return <div dangerouslySetInnerHTML={{ __html: line || ' ' }} key={index} />;
  });
};

export default function EmailDraftCard({ draft, isGenerating = false }: EmailDraftCardProps) {
  const { t } = useTranslation();
  const { copyToClipboard } = useCopyToClipboard({
    successMessage: t('assistant.emailDraft.copied'),
  });

  const displayedBody = useTypewriter(draft.body, { isGenerating });

  const handleCopy = () => copyToClipboard(draft.body);

  const handleOpenInOutlook = async () => {
    try {
      await openOrUpdateComposeWindow({
        body: addBlueColorToBody(draft.body),
        subject: draft.subject,
        to: draft.to,
        ...(draft.cc && { cc: draft.cc }),
        ...(draft.bcc && { bcc: draft.bcc }),
      });
      toast.success(t('assistant.emailDraft.applied'));
    } catch (error) {
      console.error('Failed to apply draft:', error);
      toast.error(t('assistant.emailDraft.applyFailed'));
    }
  };

  const handleReply = async () => {
    try {
      await displayReplyWithContent(addBlueColorToBody(draft.body), true);
      toast.success(t('assistant.emailDraft.replyOpened'));
    } catch (error) {
      console.error('Failed to open reply:', error);
      toast.error(t('assistant.emailDraft.replyFailed'));
    }
  };

  const handleReplyAll = async () => {
    try {
      await displayReplyAllWithContent(addBlueColorToBody(draft.body), true);
      toast.success(t('assistant.emailDraft.replyAllOpened'));
    } catch (error) {
      console.error('Failed to open reply all:', error);
      toast.error(t('assistant.emailDraft.replyAllFailed'));
    }
  };

  return (
    <div className="border-subtle bg-back-bg rounded-xl border">
      <div className="space-y-2 px-4 pt-1.5">
        {draft.to && (
          <div>
            <span className="text-secondary text-xs leading-[170%] font-semibold">
              {t('assistant.emailDraft.to')}
            </span>
            <p className="text-secondary text-xs leading-[170%] underline">{draft.to}</p>
          </div>
        )}
        {draft.cc && (
          <div>
            <span className="text-secondary text-xs leading-[170%] font-semibold">
              {t('assistant.emailDraft.cc')}
            </span>
            <p className="text-secondary text-xs leading-[170%] underline">{draft.cc}</p>
          </div>
        )}
        {draft.bcc && (
          <div>
            <span className="text-secondary text-xs leading-[170%] font-semibold">
              {t('assistant.emailDraft.bcc')}
            </span>
            <p className="text-secondary text-xs leading-[170%] underline">{draft.bcc}</p>
          </div>
        )}
        {draft.subject && (
          <div>
            <span className="text-secondary text-xs leading-[170%] font-semibold">
              {t('assistant.emailDraft.subject')}
            </span>
            <p className="text-secondary text-xs leading-[170%]">{draft.subject}</p>
          </div>
        )}
      </div>
      <div>
        <div className="border-subtle border-[0.5px]" />
        <div className="space-y-2 px-4 py-2">
          <div className="text-accent-foreground text-xs leading-[170%] whitespace-pre-wrap">
            {formatBodyWithColoredResponses(displayedBody, getUserInitials())}
            {isGenerating && (
              <span className="bg-primary ml-1 inline-block h-[1em] w-[2px] animate-pulse align-middle" />
            )}
          </div>
        </div>
      </div>

      {!isGenerating && (
        <div className="flex items-center justify-between gap-2 p-2">
          <Button
            className="hover:bg-elevated-subtle flex h-6 w-6 items-center justify-center rounded-full bg-transparent p-0 shadow-none"
            onClick={handleCopy}
          >
            <img
              alt={t('assistant.emailDraft.copyAlt')}
              className="icon-themed h-4 w-4"
              src={copyIcon}
            />
          </Button>

          {isInReadMode() && !isReadingDraft() ? (
            <div className="flex gap-2">
              <Button
                className="text-primary-foreground h-6 px-2 text-[10px]"
                onClick={handleReply}
              >
                <img
                  alt={t('assistant.emailDraft.replyAlt')}
                  className="icon-on-primary"
                  src={replyIcon}
                />
              </Button>
              <Button
                className="text-primary-foreground h-6 px-2 text-[10px]"
                onClick={handleReplyAll}
              >
                <img
                  alt={t('assistant.emailDraft.replyAllAlt')}
                  className="icon-on-primary"
                  src={replyAllIcon}
                />
              </Button>
            </div>
          ) : (
            <Button
              className="text-primary-foreground h-6 w-[85px] text-[10px]"
              onClick={handleOpenInOutlook}
            >
              {t('assistant.emailDraft.openDraft')}
              <img
                alt={t('assistant.emailDraft.openAlt')}
                className="icon-on-primary"
                src={arrowIcon}
              />
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
